import React, { useMemo, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Empty, Notice, Modal, Labelled, Label, UploadZone } from "../components/ui/index.jsx";
import { useComms, ENTRY_KINDS, DOC_KINDS } from "../lib/useComms.js";
import ProposalReview from "../components/ProposalReview.jsx";
import { readDocument, canRead } from "../lib/readDocument.js";
import { monthKey, recentMonths, monthWindow } from "../lib/report.js";
import { fmtDate } from "../lib/format.js";
import { term, termLower } from "../lib/terms.js";
import { useAuth } from "../auth/AuthProvider.jsx";

/* Where the month's words come from.
 *
 * Half the monthly deck is writing that arrives in vendor reports and meeting
 * minutes, and it exists nowhere in the registers. It lands here through the
 * month, one line at a time, so that at month end the report is assembled
 * rather than remembered.
 *
 * The documents are working material and the entries are the record. That is
 * why an entry keeps the sentence it came from and the name and date of the
 * document beside it: once the month is issued the files can go and the
 * provenance stays.
 */

const input = {
  width: "100%", padding: "7px 9px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 13.5, background: "#fff", color: T.ink,
};

const KIND_COLOUR = {
  accomplishment: "#3E6B4F",
  upcoming: "#2E4A7A",
  risk: "#8F2B25",
  decision: GOLD_INK,
  insight: T.soft,
};
const kindLabel = (k) => (ENTRY_KINDS.find(([key]) => key === k) ?? [k, k])[1];

function DocumentForm({ onClose, onSave, busy }) {
  const [draft, setDraft] = useState({ title: "", kind: "Vendor report", source: "", doc_date: "" });
  const [file, setFile] = useState(null);
  const [read, setRead] = useState(true);
  const set = (patch) => setDraft({ ...draft, ...patch });
  const readable = file ? canRead(file.name) : false;

  return (
    <Modal title="Add a document" onClose={onClose}>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.65 }}>
          The file itself is optional. Record what arrived and who it came from,
          and attach the file only if you want to be able to open it again. The
          lines you take from it are kept either way.
        </div>

        <Labelled label="What it is" hint="How you would refer to it on a call.">
          <input style={input} value={draft.title} autoFocus
            placeholder="Itron weekly report, week ending 14 August"
            onChange={(e) => set({ title: e.target.value })} />
        </Labelled>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <Labelled label="Kind">
            <select style={input} value={draft.kind} onChange={(e) => set({ kind: e.target.value })}>
              {DOC_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Labelled>
          <Labelled label="Who it came from">
            <input style={input} value={draft.source} placeholder="Itron"
              onChange={(e) => set({ source: e.target.value })} />
          </Labelled>
          <Labelled label="Its date">
            <input style={input} type="date" value={draft.doc_date}
              onChange={(e) => set({ doc_date: e.target.value })} />
          </Labelled>
        </div>

        <Labelled label="The file, if you want to keep it"
          hint="Deleted when you issue the month. What you take from it is not.">
          <input type="file" style={{ ...input, padding: 6 }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.txt,.md,.ppt,.pptx"
            onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); setRead(true); }} />
        </Labelled>

        {file && (
          readable ? (
            <label style={{ display: "flex", gap: 9, alignItems: "start", fontSize: 12.5,
                            color: T.ink, lineHeight: 1.6, cursor: "pointer" }}>
              <input type="checkbox" checked={read} style={{ marginTop: 3 }}
                onChange={(e) => setRead(e.target.checked)} />
              <span>
                Read it and suggest lines.
                <span style={{ color: T.soft }}>
                  {" "}Nothing gets saved from this until you have looked at it.
                </span>
              </span>
            </label>
          ) : (
            <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.6 }}>
              This one cannot be read automatically. Word, PowerPoint, PDF, Excel,
              CSV and plain text can be. The file is still kept and you can take
              lines from it by hand.
            </div>
          )
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn disabled={busy || !draft.title.trim()}
            onClick={() => onSave({ ...draft, doc_date: draft.doc_date || null }, file,
                                   Boolean(file) && readable && read)}>
            {busy ? "Saving..." : read && readable && file ? "Add it and read it" : "Add it"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function EntryForm({ entry, documents, workstreams, termLabel = "Workstream", onClose, onSave, busy }) {
  const [draft, setDraft] = useState(entry ?? {
    title: "", kind: "accomplishment", workstream: workstreams[0] ?? "",
    document_id: "", quote: "", source_note: "",
  });
  const set = (patch) => setDraft({ ...draft, ...patch });

  /* Picking the document fills in the source note, so the provenance is
     written down without anybody typing it, and survives the file being
     deleted at the end of the month. */
  const pickDoc = (id) => {
    const doc = documents.find((d) => d.id === id);
    set({
      document_id: id || null,
      source_note: doc
        ? [doc.title, doc.doc_date ? fmtDate(doc.doc_date) : ""].filter(Boolean).join(", ")
        : draft.source_note,
    });
  };

  return (
    <Modal title={entry ? "Edit this line" : "Add a line"} onClose={onClose} wide>
      <div style={{ display: "grid", gap: 12 }}>
        <Labelled label="What happened" hint="One line, the way it would read on the slide.">
          <input style={input} value={draft.title} autoFocus
            placeholder="SIT for FM 9 (Billing) is complete"
            onChange={(e) => set({ title: e.target.value })} />
        </Labelled>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
          <Labelled label="Which box it belongs in">
            <select style={input} value={draft.kind} onChange={(e) => set({ kind: e.target.value })}>
              {ENTRY_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </Labelled>
          <Labelled label={termLabel}>
            {workstreams.length ? (
              <select style={input} value={draft.workstream ?? ""}
                onChange={(e) => set({ workstream: e.target.value })}>
                <option value="">Not tied to one</option>
                {workstreams.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            ) : (
              <input style={input} value={draft.workstream ?? ""}
                onChange={(e) => set({ workstream: e.target.value })} />
            )}
          </Labelled>
          <Labelled label="Which document said it">
            <select style={input} value={draft.document_id ?? ""}
              onChange={(e) => pickDoc(e.target.value)}>
              <option value="">Not from a document</option>
              {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </Labelled>
        </div>

        <Labelled label="The line it came from"
          hint="Kept for good, so when somebody asks where this came from you can show them the sentence even after the file is gone.">
          <textarea style={{ ...input, minHeight: 70, fontFamily: "inherit" }}
            value={draft.quote ?? ""} onChange={(e) => set({ quote: e.target.value })} />
        </Labelled>

        <Labelled label="Where it came from">
          <input style={input} value={draft.source_note ?? ""}
            placeholder="Itron weekly report, 14 August 2026"
            onChange={(e) => set({ source_note: e.target.value })} />
        </Labelled>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn disabled={busy || !draft.title.trim()}
            onClick={() => onSave({ ...draft, confirmed_at: new Date().toISOString() })}>
            {busy ? "Saving..." : "Save"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function Comms({ contract }) {
  const [period, setPeriod] = useState(monthKey());
  const [addingDoc, setAddingDoc] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [reading, setReading] = useState("");
  const [beats, setBeats] = useState(0);
  const [readError, setReadError] = useState("");
  const [proposals, setProposals] = useState(null);
  const c = useComms(contract?.id, period);
  const { canEdit } = useAuth();

  const months = useMemo(() => recentMonths(monthKey(), 12).reverse(), []);
  const workstreams = contract?.workstream_list ?? [];
  const label = monthWindow(period).label;

  /* Reading a document. The file either came straight off the picker, in
     which case it never had to be uploaded at all, or it is fetched back out
     of storage for something added earlier. Either way nothing is saved: the
     answer goes on screen for a person to agree with. */
  const read = async (doc, file, fileName) => {
    setReadError("");
    setBeats(0);
    setReading(doc?.id || "new");
    try {
      const found = await readDocument({
        contractId: contract.id, period, doc, file, fileName,
        onProgress: () => setBeats((n) => n + 1),
      });
      setProposals({
        ...found,
        documentId: doc?.id ?? null,
        sourceNote: [doc?.title, doc?.doc_date ? fmtDate(doc.doc_date) : ""]
          .filter(Boolean).join(", "),
      });
    } catch (failed) {
      setReadError(failed.message || "The document could not be read.");
    } finally {
      setReading("");
    }
  };

  const readStored = async (doc) => {
    setReadError("");
    setReading(doc.id);
    const blob = await c.downloadDocument(doc);
    setReading("");
    if (!blob) { setReadError("That file could not be fetched back."); return; }
    await read(doc, blob, doc.storage_path.split("/").pop());
  };

  /* Keeping what came back. Saved one at a time through the same path a
     typed line takes, so the rules and the actor stamp are identical. */
  const keepProposals = async (chosen) => {
    for (const entry of chosen) {
      if (!(await c.saveEntry(entry))) return;
    }
    setProposals(null);
  };

  const byKind = useMemo(() => {
    const out = new Map(ENTRY_KINDS.map(([k]) => [k, []]));
    for (const e of c.entries ?? []) {
      if (!out.has(e.kind)) out.set(e.kind, []);
      out.get(e.kind).push(e);
    }
    return out;
  }, [c.entries]);

  if (!c.documents || !c.entries) return <Empty text="Loading..." />;

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {addingDoc && (
        <DocumentForm busy={c.busy || reading === "new"} onClose={() => setAddingDoc(false)}
          onSave={async (d, f, readIt) => {
            const saved = await c.addDocument(d, f);
            if (!saved) return;
            setAddingDoc(false);
            if (readIt && f) await read(saved, f, f.name);
          }} />
      )}
      {editing && (
        <EntryForm entry={editing.id ? editing : null} documents={c.documents}
          workstreams={workstreams} termLabel={term(contract)}
          busy={c.busy} onClose={() => setEditing(null)}
          onSave={async (d) => { if (await c.saveEntry(d)) setEditing(null); }} />
      )}
      {proposals && (
        <ProposalReview
          summary={proposals.summary} entries={proposals.entries}
          workstreams={workstreams} label={term(contract)}
          sourceNote={proposals.sourceNote}
          documentId={proposals.documentId} busy={c.busy}
          onClose={() => setProposals(null)} onSave={keepProposals} />
      )}
      {confirmDoc && (
        <Modal title="Remove this document?" onClose={() => setConfirmDoc(null)}>
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.7 }}>
            <strong>{confirmDoc.title}</strong> and its file go. Every line you took
            from it stays exactly where it is, still carrying the sentence and
            where it came from.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setConfirmDoc(null)}>Cancel</Btn>
            <Btn kind="danger" onClick={async () => {
              await c.removeDocument(confirmDoc); setConfirmDoc(null);
            }}>Remove it</Btn>
          </div>
        </Modal>
      )}

      <Panel
        title={`Communications · ${label}`}
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}
              style={{ ...input, width: 120, padding: "5px 8px" }}>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {canEdit && (
              <>
                <Btn small kind="gold" onClick={() => setAddingDoc(true)}>Add a document</Btn>
                <Btn small onClick={() => setEditing({})}>Add a line</Btn>
              </>
            )}
          </div>
        }
      >
        <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7 }}>
          What arrived this month, and what it said. The lines you keep here fill
          the parts of the monthly report that no register can answer: the
          executive summary, the top five outcomes, and each {termLower(contract)}'s
          accomplishments, upcoming tasks and risks.
          <br /><br />
          Documents are working material and can be deleted once the month is
          issued. The lines are the record and are kept for good, each with the
          sentence it came from, so the provenance survives the file going.
        </div>
        {c.error && <div style={{ marginTop: 12 }}><Notice kind="error">{c.error}</Notice></div>}
        {readError && <div style={{ marginTop: 12 }}><Notice kind="error">{readError}</Notice></div>}
        {reading && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="info">
              Reading it now{".".repeat((beats % 3) + 1)} A long report takes
              half a minute or so. Leave this page open.
            </Notice>
          </div>
        )}
      </Panel>

      <Panel title="What arrived" right={<Chip color={T.steel}>{c.documents.length}</Chip>}>
        {c.documents.length === 0
          ? <Empty text="Nothing recorded for this month yet. Add the vendor reports and minutes as they come in." />
          : (
            <div style={{ display: "grid", gap: 8 }}>
              {c.documents.map((d) => {
                const lines = (c.entries ?? []).filter((e) => e.document_id === d.id).length;
                return (
                  <div key={d.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 12, padding: "9px 2px", borderBottom: `1px solid ${T.line}`,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.navy }}>{d.title}</div>
                      <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>
                        {[d.kind, d.source, d.doc_date ? fmtDate(d.doc_date) : null]
                          .filter(Boolean).join(" · ")}
                        {" · "}{lines} line{lines === 1 ? "" : "s"} taken
                        {!d.storage_path && " · no file kept"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, whiteSpace: "nowrap" }}>
                      {d.storage_path && (
                        <Btn small kind="ghost" onClick={async () => {
                          const url = await c.openDocument(d);
                          if (url) window.open(url, "_blank", "noopener");
                        }}>Open</Btn>
                      )}
                      {canEdit && d.storage_path && canRead(d.storage_path) && (
                        <Btn small kind="gold" disabled={Boolean(reading)}
                          onClick={() => readStored(d)}>
                          {reading === d.id ? "Reading..." : "Read it"}
                        </Btn>
                      )}
                      {canEdit && <Btn small kind="ghost"
                        onClick={() => setEditing({ document_id: d.id, kind: "accomplishment",
                          workstream: workstreams[0] ?? "", title: "", quote: "",
                          source_note: [d.title, d.doc_date ? fmtDate(d.doc_date) : ""].filter(Boolean).join(", ") })}>
                        Take a line
                      </Btn>}
                      {canEdit && (
                        <Btn small kind="danger" onClick={() => setConfirmDoc(d)}>Remove</Btn>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </Panel>

      <Panel title="What it said" right={<Chip color={GOLD_INK}>{c.entries.length} lines</Chip>}>
        {c.entries.length === 0
          ? <Empty text="No lines yet. Add a document and take lines from it, or add a line straight in." />
          : (
            <div style={{ display: "grid", gap: 16 }}>
              {[...byKind].filter(([, list]) => list.length).map(([kind, list]) => (
                <div key={kind}>
                  <div style={{ marginBottom: 6 }}>
                    <Label color={KIND_COLOUR[kind] ?? T.soft}>
                      {kindLabel(kind)} · {list.length}
                    </Label>
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {list.map((e) => (
                      <div key={e.id} style={{
                        display: "flex", justifyContent: "space-between", gap: 12,
                        padding: "8px 10px", background: T.chip, borderRadius: 4,
                        borderLeft: `3px solid ${KIND_COLOUR[kind] ?? T.line}`,
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.5 }}>{e.title}</div>
                          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 3 }}>
                            {[e.workstream, e.source_note].filter(Boolean).join(" · ") || "No source recorded"}
                          </div>
                          {e.quote && (
                            <div style={{ fontSize: 12, color: T.soft, marginTop: 4,
                                          fontStyle: "italic", lineHeight: 1.55 }}>
                              "{e.quote}"
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, whiteSpace: "nowrap", alignItems: "start" }}>
                          {canEdit && (
                            <>
                              <Btn small kind="ghost" onClick={() => setEditing(e)}>Edit</Btn>
                              <Btn small kind="danger" onClick={() => c.removeEntry(e)}>Remove</Btn>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </Panel>
    </div>
  );
}
